import axios from 'axios'
import { message } from 'antd'
import qs from 'qs'
import jsCookie from 'js-cookie'
import { getBaseUrl } from '../utils/baseUrl'
import userUtil from '../util/UserUtil'
import errorLogger, { summarizeError } from '../util/errorLogger'

const instance = axios.create({
    baseURL: getBaseUrl(),
    timeout: 15000,
    headers: {
        'Content-Type': 'application/json',
    },
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'indices', allowDots: true })
})

function decodeJwtPayload(token) {
    if (!token || typeof token !== 'string' || token.split('.').length !== 3) {
        return null;
    }
    try {
        const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
        const json = decodeURIComponent(
            atob(padded)
                .split('')
                .map(char => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`)
                .join('')
        );
        return JSON.parse(json);
    } catch (e) {
        return null;
    }
}

// 请求超时提示
instance.interceptors.request.use(function (config) {
    config._startTime = Date.now();
    return config;
});

instance.interceptors.request.use(
    config => {
        if (config.skipAuth) {
            delete config.headers['Authorization'];
            errorLogger.info('SJ-API-001', 'skip auth header for request', {
                url: config.url,
            });
        } else {
            const location = window.app && window.app.history && window.app.history.location
                ? window.app.history.location
                : window.location
            const searchParams = new URLSearchParams(location.search)
            const tokenCandidates = [
                {source: 'url-token', token: searchParams.get('token')},
                {source: 'url-tn', token: searchParams.get('tn')},
                {source: 'localStorage', token: userUtil.getStoredLogin()?.token},
                {source: 'cookie', token: jsCookie.get('token')},
            ];
            const validToken = tokenCandidates.find(item => userUtil.isValidAuthToken(item.token));
            const hasInvalidToken = tokenCandidates.some(item => item.token && !userUtil.isValidAuthToken(item.token));

            if (validToken) {
                config.headers['Authorization'] = `Bearer ${validToken.token.trim()}`;
            }

            if (hasInvalidToken) {
                userUtil.clearTokenCookies();
                errorLogger.warn('SJ-API-002', 'ignored invalid auth token candidate', {
                    url: config.url,
                    sources: tokenCandidates
                        .filter(item => item.token && !userUtil.isValidAuthToken(item.token))
                        .map(item => item.source),
                    selected: validToken && validToken.source,
                });
            }
        }

        config.params = config.params || {}
        if (!config.params.channel) {
            config.params.channel = 'pc'
        }
        if (!config.params.platform) {
            config.params.platform = 'pc'
        }
        if (!config.params.appId) {
            config.params.appId = window.app && window.app.appId ? window.app.appId : ''
        }
        if (!config.params.biz && !config.url.includes('biz=')) {
            config.params.biz = window.app && window.app.biz ? window.app.biz : 'time'
        }

        return config
    },
    error => {
        errorLogger.error('SJ-API-003', 'axios request interceptor failed', summarizeError(error));
        return Promise.reject(error)
    }
)

instance.interceptors.response.use(
    response => {
        if (response.headers['token']) {
            const token = response.headers['token'];
            const stored = userUtil.getStoredLogin();
            if (userUtil.isValidAuthToken(token) && stored && stored.userNum) {
                userUtil.saveLogin(stored.userNum, token);
            } else if (!userUtil.isValidAuthToken(token)) {
                userUtil.clearTokenCookies();
            } else {
                jsCookie.set('token', token, { expires: 1 })
            }
        }

        const isLoginResponse = response.config &&
            response.config.url &&
            response.config.url.includes('/api/login/doLogin');
        const headerToken = response.headers['token'];
        const tokenPayload = userUtil.isValidAuthToken(headerToken) ? decodeJwtPayload(headerToken) : null;

        if (isLoginResponse && tokenPayload) {
            return {
                token: headerToken,
                userNum: tokenPayload.userNum || tokenPayload.sub,
                cookieLogin: true,
            };
        }

        if (response.data && response.data.code) {
            if ((response.data.code === 401 || response.data.code === 301) && window.location.pathname !== '/login') {
                if (response.config && response.config.skipLoginModal) {
                    errorLogger.info('SJ-API-004', 'skip global login modal for auth check', {
                        url: response.config.url,
                        code: response.data.code,
                        msg: response.data.msg,
                    });
                    return response.data;
                }
                userUtil.clearLogin();
                errorLogger.warn('SJ-API-005', 'auth response requires login', {
                    url: response.config && response.config.url,
                    code: response.data.code,
                    msg: response.data.msg,
                    hasShowLogin: !!(window.app && window.app.showLogin),
                    pathname: window.location.pathname,
                });
                if (window.autosave) {
                    message.warning('您的登录信息出现异常，请注意手动备份当前文档后刷新网页并重新登录')
                } else if (window.app && window.app.showLogin) {
                    window.app.showLogin(response.config.onLogin)
                } else {
                    window.location.href = '/login'
                }
                if (response.config && response.config.rejectOnBusinessError) {
                    const authError = new Error(response.data.msg || '登录已失效，请重新登录');
                    authError.name = 'AuthError';
                    authError.responseData = response.data;
                    authError.response = response;
                    return Promise.reject(authError);
                }
                return response.data;
            } else if (response.data.code === 1) {
                errorLogger.info('SJ-API-006', 'business info response', {
                    url: response.config && response.config.url,
                    code: response.data.code,
                    msg: response.data.msg,
                });
                if (response.data.msg) {
                    message.info(`${response.data.msg}`)
                }
            } else if (response.data.code === 3) {
                if (response.data.data) {
                    const isLocalDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
                    window.location.href = isLocalDev
                        ? `${window.location.protocol}//${window.location.host}/api/fileDown/${response.data.data}`
                        : `/api/fileDown/${response.data.data}`;
                }
            } else if (response.data.code !== 0) {
                if (response.config.url.includes('/api/wx/checkLogin')) {
                    errorLogger.info('SJ-API-007', 'checkLogin returned non-success business code', {
                        url: response.config && response.config.url,
                        code: response.data.code,
                        msg: response.data.msg,
                    });
                    return response.data
                } else {
                    errorLogger.warn('SJ-API-008', 'business api returned non-success code', {
                        url: response.config && response.config.url,
                        code: response.data.code,
                        msg: response.data.msg,
                    });
                    message.error(`[${response.data.code}] ${response.data.msg}`)
                    if (response.config && response.config.rejectOnBusinessError) {
                        const businessError = new Error(response.data.msg || `业务接口返回错误：${response.data.code}`);
                        businessError.name = 'BusinessError';
                        businessError.responseData = response.data;
                        businessError.response = response;
                        return Promise.reject(businessError);
                    }
                }
            }
        }
        if (isLoginResponse && response.data && response.data.code === 0 && !response.data.data && tokenPayload) {
            return {
                token: headerToken,
                userNum: tokenPayload && (tokenPayload.userNum || tokenPayload.sub),
                cookieLogin: true,
            };
        }

        return response.data.data
    },
    error => {
        // 请求超时处理
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            errorLogger.error('SJ-API-009', 'api request timeout', summarizeError(error));
            message.error('请求超时，请检查网络后重试', 3);
            return Promise.reject(error);
        }

        const status = error?.response?.status;
        const url = error?.config?.url || '';

        // 开发环境后端未启动时，静默处理避免弹窗干扰
        // 但登录相关接口需要把错误抛出去，让登录组件给出准确的错误提示
        const isLoginRelated = url.includes('/api/login/') || url.includes('/api/wx/checkLogin');
        if (window.location.hostname === 'localhost' && !isLoginRelated) {
            errorLogger.warn('SJ-API-010', 'local dev api request failed', {
                url,
                ...summarizeError(error),
            });
            // 返回 null 避免 Promise 拒绝触发 React error overlay
            return Promise.resolve(null);
        }

        // 静默处理：未登录时的用户信息相关 404
        const silent404Paths = [
            '/api/wx/mp/user',
            '/api/wx/checkLogin',
            '/api/login/loginOut',
            '/api/wx/yuxie/userMenu',
            '/api/wx/yuxie/dailies',
            '/api/live/dailies',
            '/api/read/dailies',
            '/api/wx/publish/dailies',
            '/api/wx/time/reports',
            '/api/wx/timelog/list',
            '/api/wx/timelog/chartData',
        ];
        if (status === 404 && silent404Paths.some(p => url.includes(p))) {
            errorLogger.warn('SJ-API-011', 'silent 404 for unauthenticated path', {
                url,
                ...summarizeError(error),
            });
            return Promise.resolve(null);
        }

        // 网络错误
        if (status === 0 || !navigator.onLine) {
            errorLogger.error('SJ-API-012', 'network unavailable or request blocked', summarizeError(error));
            message.warning('网络连接异常，请检查网络后重试');
        }
        // 服务器错误
        else if (status >= 500) {
            errorLogger.error('SJ-API-013', 'server api error', summarizeError(error));
            message.error('服务器繁忙，请稍后重试');
        }
        // 其他错误
        else {
            errorLogger.error('SJ-API-014', 'api request failed', summarizeError(error));
            message.error(`${error.message || '请求失败'}`);
        }
        return Promise.reject(error);
    }
)

export default instance
